import type { Artwork, ArtworkPlacementTransform, ArtworkWrapper } from "@/types/artworksData";

type PlacementSource = "artwork" | "wrapper" | "default";

export type ResolvedArtworkWrapperTransform = {
  transform: ArtworkPlacementTransform;
  source: PlacementSource;
  artworkId?: string;
};

export type MovedArtworkPayload = {
  slotTarget: number;
  artworkId?: string;
  position: ArtworkPlacementTransform["position"];
  rotation: ArtworkPlacementTransform["rotation"];
  scale: ArtworkPlacementTransform["scale"];
  pedestalDiameter?: number;
};

export const defaultArtworkTransform = (): ArtworkPlacementTransform => ({
  position: { x: 0, y: 0, z: 0 },
  rotation: { x: 0, y: 0, z: 0 },
  scale: { x: 1, y: 1, z: 1 },
});

export const normalizeArtworkId = (artwork: Partial<Artwork> | string | null | undefined): string | undefined => {
  if (!artwork) return undefined;
  if (typeof artwork === "string") return artwork;
  const candidate = (artwork as any)._id;
  if (!candidate) return undefined;
  return String(candidate);
};

export const isPseudoMoMSpaceData = (spaceData: any): boolean => {
  return spaceData?.subtype === "pseudoMoM";
};

export const getActiveArtworkId = (artworkWrapper?: Partial<ArtworkWrapper> | null): string | undefined => {
  if (!artworkWrapper || !Array.isArray(artworkWrapper.artworks)) return undefined;
  const activeArtworkIndex = Number(artworkWrapper.activeArtwork);
  if (!Number.isInteger(activeArtworkIndex)) return undefined;
  return normalizeArtworkId(artworkWrapper.artworks[activeArtworkIndex]);
};

export const resolveArtworkWrapperTransform = (
  artworkWrapper?: Partial<ArtworkWrapper> | null,
  spaceData?: any
): ResolvedArtworkWrapperTransform => {
  const artworkId = getActiveArtworkId(artworkWrapper);

  if (isPseudoMoMSpaceData(spaceData) && artworkId) {
    const artworkTransform = artworkWrapper?.artworkTransforms?.[artworkId];
    if (artworkTransform) {
      return {
        transform: artworkTransform,
        source: "artwork",
        artworkId,
      };
    }
  }

  if (artworkWrapper?.transforms) {
    return {
      transform: {
        ...defaultArtworkTransform(),
        ...artworkWrapper.transforms,
        position: {
          ...defaultArtworkTransform().position,
          ...(artworkWrapper.transforms.position || {}),
        },
        rotation: {
          ...defaultArtworkTransform().rotation,
          ...(artworkWrapper.transforms.rotation || {}),
        },
        scale: {
          ...defaultArtworkTransform().scale,
          ...(artworkWrapper.transforms.scale || {}),
        },
      },
      source: "wrapper",
      artworkId,
    };
  }

  return {
    transform: defaultArtworkTransform(),
    source: "default",
    artworkId,
  };
};

export const createMovedArtworkKey = (slotTarget: number, artworkId?: string): number | string => {
  return artworkId ? `${slotTarget}:${artworkId}` : slotTarget;
};

export const buildMovedArtworkPayloads = (changedArtworks: Map<any, any>): MovedArtworkPayload[] => {
  const artworksToMove: MovedArtworkPayload[] = [];

  changedArtworks.forEach((value, key) => {
    const slotTarget = Number(value?.slotTarget ?? key);
    if (!Number.isFinite(slotTarget)) return;

    const artworkToMove: MovedArtworkPayload = {
      slotTarget,
      ...(value?.artworkId ? { artworkId: String(value.artworkId) } : {}),
      position: value.position,
      rotation: value.rotation,
      scale: value.scale,
    };

    if (value.pedestalDiameter !== null && value.pedestalDiameter !== undefined) {
      artworkToMove.pedestalDiameter = value.pedestalDiameter;
    }

    artworksToMove.push(artworkToMove);
  });

  return artworksToMove;
};
